/**
 * Utilidades de autenticación
 * 
 * Funciones para verificar tokens JWT, validar permisos, y manejar sesiones
 */

export interface TokenPayload {
  sub: string; // owner_id
  email: string;
  name: string;
  acl: string;
  exp: number;
}

/**
 * Obtiene el token JWT de las cookies.
 * NOTA: Con HttpOnly habilitado, la cookie no es legible desde JavaScript client-side.
 * Esta función solo funciona en contextos server-side (middleware, API routes).
 * En client-side, usar isAuthenticated() que se basa en localStorage.
 */
export function getToken(): string | null {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('auth_token='));
  const token = cookie ? cookie.substring('auth_token='.length) : undefined;

  return token || null;
}

/**
 * Decodifica un token JWT sin verificar (solo para lectura)
 * Para verificación real, se debe hacer en el backend
 */
export function decodeToken(token: string): TokenPayload | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decodificando token:', error);
    return null;
  }
}

/**
 * Verifica si un token está expirado
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeToken(token);
  if (!payload || !payload.exp) return true;
  
  const expirationTime = payload.exp * 1000; // Convertir a milisegundos
  return Date.now() >= expirationTime;
}

/**
 * Verifica si el usuario tiene un ACL específico
 */
export function hasACL(userACL: string | null | undefined, requiredACL: string | string[]): boolean {
  if (!userACL) return false;
  
  if (Array.isArray(requiredACL)) {
    return requiredACL.includes(userACL);
  }
  
  return userACL === requiredACL;
}

/**
 * Obtiene información del usuario desde el token
 */
export function getUserFromToken(): TokenPayload | null {
  const token = getToken();
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    return null;
  }
  
  return decodeToken(token);
}

/**
 * Limpia la sesión del usuario
 */
export function clearSession(): void {
  if (typeof document !== 'undefined') {
    // max-age=0 + expires en el pasado: doble compatibilidad con browsers que no entienden max-age
    document.cookie = 'auth_token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    try {
      localStorage.removeItem('currentUser');
    } catch {
      // Safari en modo privado puede lanzar QuotaExceededError; ignorar
    }
  }
}

/**
 * Verifica si el usuario está autenticado.
 * Con HttpOnly cookies, no podemos leer el token desde JS.
 * Usamos la presencia de currentUser en localStorage como indicador.
 * La verificación real del token se hace en el backend.
 */
export function isAuthenticated(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const userStr = localStorage.getItem('currentUser');
    return !!userStr;
  } catch {
    // localStorage no disponible (Safari privado, browser restrictivo)
    return false;
  }
}
